import { TFunction } from "i18next";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import VinDetail from "@/models/VinDetail";

/** One line of the vehicle's identification: what it is called, and what it says. */
export interface VehicleField {
  label: string;
  value: string;
}

/**
 * What identifies the vehicle a checklist belongs to.
 *
 * Shared by the panel above the checklist and by the exported sheet, so the two cannot come to
 * disagree about which fields identify a vehicle — a difference nobody would notice until an
 * archived sheet turned out to be missing the one field someone needed.
 *
 * @param device - The selected vehicle.
 * @param t - Translator.
 * @param notFound - What to show for a field the database does not carry.
 * @param vin - What Geotab reads out of the vehicle identification number, where it answered.
 * Left out entirely rather than shown empty: a row of "not found" against a VIN that simply has
 * not been decoded yet says nothing about the vehicle.
 */
export function vehicleIdentification(
  device: DeviceWithVehicleInfo | null | undefined,
  t: TFunction,
  notFound: string,
  vin?: VinDetail,
): VehicleField[] {
  const field = (value: string | undefined) => value?.trim() || notFound;

  const decoded: VehicleField[] = [];
  const decodedField = (label: string, value: string | undefined) => {
    if (value?.trim()) {
      decoded.push({ label, value: value.trim() });
    }
  };

  decodedField(t("liveData.checklist.vinManufacturer", "Manufacturer"), vin?.manufacturer);
  decodedField(t("liveData.checklist.vinMake", "Make"), vin?.make);
  decodedField(t("liveData.checklist.vinModel", "Model"), vin?.model);
  decodedField(t("liveData.checklist.vinVehicleType", "Vehicle type"), vin?.vehicleType);
  decodedField(t("liveData.checklist.vinYear", "Model year"), vin?.year);

  return [
    { label: t("liveData.checklist.assetName", "Asset name"), value: field(device?.name) },
    { label: t("vin", "VIN"), value: field(device?.vehicleIdentificationNumber) },
    {
      label: t("liveData.checklist.telematicsSerialNumber", "Telematics serial number"),
      value: field(device?.serialNumber),
    },
    {
      label: t("liveData.checklist.licensePlate", "License plate"),
      value: field(device?.licensePlate),
    },
    ...decoded,
  ];
}
